import { useState, useEffect } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase';

// Mirror of the server-side reader in packages/functions/shared/feature_flags.py.
// All flags live in one doc, config/featureFlags, as a flat map of
// flagName -> bool. Flip a flag in the Firebase console and every surface
// updates live, no redeploy.
export const FEATURE_FLAGS_COLLECTION = 'config';
export const FEATURE_FLAGS_DOCUMENT = 'featureFlags';
export const PUSH_NOTIFICATIONS_FLAG = 'pushNotifications';
// Mirror of shared/storage_grace.py::STORAGE_GRACE_FLAG — the #346
// downgrade-lifecycle kill switch. Defaults OFF everywhere it is read.
export const STORAGE_GRACE_FLAG = 'storageGraceEnabled';

// Subscribe to a single global feature flag. Falls back to `defaultValue`
// when the doc is absent, the field is missing, the stored value is not a
// real boolean, or the read errors — so adding a flag check never changes
// behavior until an explicit boolean is written. `enabled` holds the
// fallback while loading, so a feature that defaults off stays hidden
// rather than flashing on then disappearing.
//
// `subscribe` lets callers skip the read entirely (e.g. for signed-out
// visitors, since the flags doc requires authentication to read).
export default function useFeatureFlag(flag, { defaultValue = false, subscribe = true } = {}) {
  const [enabled, setEnabled] = useState(defaultValue);
  const [loading, setLoading] = useState(subscribe);

  useEffect(() => {
    if (!subscribe) {
      setEnabled(defaultValue);
      setLoading(false);
      return undefined;
    }

    const ref = doc(db, FEATURE_FLAGS_COLLECTION, FEATURE_FLAGS_DOCUMENT);
    const unsubscribe = onSnapshot(ref, (snap) => {
      const data = snap.exists() ? snap.data() : {};
      const value = data[flag];
      setEnabled(typeof value === 'boolean' ? value : defaultValue);
      setLoading(false);
    }, (error) => {
      console.error(`Error reading feature flag "${flag}":`, error);
      setEnabled(defaultValue);
      setLoading(false);
    });

    return () => unsubscribe();
  }, [flag, defaultValue, subscribe]);

  return { enabled, loading };
}

// Global kill-switch for push notifications. Defaults to OFF when unset —
// matches push_notifications_enabled in the server-side reader, so the UI
// hides push until config/featureFlags.pushNotifications is set to true.
export function usePushNotificationsEnabled(options = {}) {
  return useFeatureFlag(PUSH_NOTIFICATIONS_FLAG, { defaultValue: false, ...options });
}
